import classNames from 'classnames';
import {useMediaQuery} from 'hooks/useMediaQuery';
import {breakpoints} from 'lib/breakpoints';
import {formatMoney} from 'lib/formatters';
import {unicodeSymbols} from 'lib/unicodeSymbols';
import {FC, useMemo} from 'react';

import {Checkbox, CheckboxSize} from 'components/Checkbox/Checkbox';
import {Counter} from 'components/Counter/Counter';
import {DiscountLabel} from 'components/DiscountLabel/DiscountLabel';
import {Icon} from 'components/Icon/Icon';
import {Text} from 'components/Text/Text';

import styles from './CartProductItem.module.css';

export type CartProductItemData = {
    id: number;
    title: string;
    link: string;
    article: string;
    preview: string;
    count: number;
    price: {
        old: number;
        current: number;
    };
    counter: {
        min: number;
        addBy: number;
    };
};

type Props = {
    className?: string;
    data: CartProductItemData;
    checked: boolean;
    removed?: boolean;
    readonly?: boolean;
    amount: number;
    onChangeCount?: (count: number) => void;
    onRemove?: () => void;
    onRestore?: () => void;
    onCheck?: (checked: boolean) => void;
};

export const CartProductItem: FC<Props> = ({
    className,
    data,
    checked,
    removed,
    amount,
    readonly,
    onChangeCount,
    onRemove,
    onRestore,
    onCheck,
}) => {
    const priceIsChanged = data.price.old !== data.price.current;
    const isMobile = useMediaQuery(breakpoints.down('sm'));

    const discount = useMemo(() => {
        if (!priceIsChanged) {
            return 0;
        }

        return Math.ceil(
            Math.abs(
                ((data.price.old - data.price.current) / data.price.old) * 100,
            ),
        );
    }, [priceIsChanged, data.price]);

    return (
        <div
            className={classNames(styles.block, className, {
                [styles.removed]: removed,
                [styles.readonly]: readonly,
            })}
        >
            {!readonly && onCheck && (
                <div className={styles.checkboxColumn}>
                    <Checkbox
                        onCheck={onCheck}
                        name={`product_${data.id}`}
                        checked={checked}
                        size={isMobile ? CheckboxSize.L : CheckboxSize.S}
                    />
                </div>
            )}
            <img src={data.preview} className={styles.preview} alt="" />
            <div className={styles.productInfo}>
                <Text.Regular14 className={styles.code}>
                    Арт: {data.article}
                </Text.Regular14>
                {data.link ? (
                    <Text.Bold14>
                        <a
                            className={styles.link}
                            href={data.link}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {data.title}
                        </a>
                    </Text.Bold14>
                ) : (
                    <Text.Bold14>{data.title}</Text.Bold14>
                )}
            </div>
            <div className={styles.counterColumn}>
                <Counter
                    className={classNames(styles.counter, {
                        [styles.readonly]: readonly,
                    })}
                    value={amount}
                    onChange={onChangeCount}
                    readonly={readonly}
                    step={data.counter.addBy}
                    minValue={data.counter.min}
                />
                <Text.Regular14 className={styles.itemPrice}>
                    {formatMoney(data.price.current)} / шт.
                </Text.Regular14>
            </div>
            <div
                className={classNames(styles.prices, {
                    [styles.withSale]: priceIsChanged,
                })}
            >
                {priceIsChanged && (
                    <div className={styles.oldPrice}>
                        <Text.Regular14 className={styles.oldPriceText}>
                            {formatMoney(amount * data.price.old)}
                        </Text.Regular14>
                        {Boolean(discount) && (
                            <DiscountLabel inactive={removed}>
                                {unicodeSymbols.ndash}
                                {discount}%
                            </DiscountLabel>
                        )}
                    </div>
                )}
                <Text.Bold18 className={styles.newPrice}>
                    {formatMoney(amount * data.price.current)}
                </Text.Bold18>
            </div>
            {!readonly && (
                <div className={styles.controls}>
                    {!removed && (
                        <Icon
                            name="delete"
                            className={styles.controlIcon}
                            onClick={onRemove}
                        />
                    )}
                    {removed && (
                        <Icon
                            name="add"
                            className={styles.controlIcon}
                            onClick={onRestore}
                        />
                    )}
                </div>
            )}
        </div>
    );
};
